import { useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';

import { getPetitionShareCounts } from './api';
import type { UsePetitionShareCountsResult } from './types';

const QUERY_KEY = 'petition-share-counts';

type Options = Readonly<{
	enabled?: boolean;
}>;

export function usePetitionShareCounts({ enabled = true }: Options = {}): UsePetitionShareCountsResult {
	const { data } = usePetitionDetailsPageData();
	const slugOrId = data.petition.id;
	const utilityContext = useUtilityContext();

	const {
		data: queryData,
		isLoading,
		isFetching,
		isFetched,
		error,
		refetch: queryRefetch,
	} = useQuery({
		queryKey: [QUERY_KEY, { slugOrId }],
		queryFn: async () => getPetitionShareCounts({ slugOrId, utilityContext }),
		// No staleTime - always fetch fresh data since backend handles caching
		staleTime: 0,
		enabled,
	});

	const refetch = useCallback(async () => {
		await queryRefetch();
	}, [queryRefetch]);

	return {
		totalShareCount: queryData?.totalShareCount ?? null,
		totalShareCountLocalized: queryData?.totalShareCountLocalized ?? null,
		shareCountsByChannel: queryData?.shareCountsByChannel ?? [],
		isLoading: isLoading || isFetching,
		isLoaded: isFetched,
		error: error ?? null,
		refetch,
	};
}

export type { UsePetitionShareCountsResult } from './types';
